import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { orgApi, schedulingApi } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { actionsStyle, cancelButton, errorBanner, formStyle, generatePassword, hintStyle, inputStyle, labelStyle, pageStyle, primaryButton } from '../formUi';

const storesForManager = (manager: any) => {
  if (!manager) return [];
  const linked = (manager.managerStores || []).map((ms: any) => ms.store).filter(Boolean);
  if (manager.store && !linked.some((store: any) => store.id === manager.store.id)) linked.push(manager.store);
  return linked;
};

const EmployeesPage: React.FC<{ unassignedOnly?: boolean; storeId?: string }> = ({ unassignedOnly, storeId }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<any>({ temporaryPassword: '', role: 'employee' });
  const [selectedManager, setSelectedManager] = useState<Record<string, string>>({});
  const [selectedStore, setSelectedStore] = useState<Record<string, string>>({});
  const [assigning, setAssigning] = useState<string | null>(null);
  const [schedulingResult, setSchedulingResult] = useState<{ employeeId: string; result: any } | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  const load = async () => {
    try {
      setError(null);
      const params: any = {};
      if (user?.role === 'Managers') params.createdBy = user.id;
      if (storeId) params.storeId = storeId;
      const requests: Promise<any>[] = [
        orgApi.listEmployees(params),
        user?.role === 'Managers' ? orgApi.myStores() : orgApi.listStores(),
      ];
      if (unassignedOnly) requests.push(orgApi.listManagers());
      const [emps, storeList, managerList] = await Promise.all(requests);
      setItems(unassignedOnly ? emps.filter((e: any) => !e.managerId) : emps);
      setStores(storeList);
      if (unassignedOnly) setManagers(managerList || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, storeId, unassignedOnly]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await orgApi.createEmployee({
        ...form,
        storeId: form.storeId || stores[0]?.id,
        temporaryPassword: form.temporaryPassword,
        loginUrl: window.location.origin,
      });
      setCreating(false);
      setForm({ temporaryPassword: '', role: 'employee' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const chooseManager = (employeeId: string, managerId: string) => {
    const manager = managers.find((m) => m.id === managerId);
    const managerStores = storesForManager(manager);
    setSelectedManager((prev) => ({ ...prev, [employeeId]: managerId }));
    setSelectedStore((prev) => ({
      ...prev,
      [employeeId]: managerStores.length === 1 ? managerStores[0].id : '',
    }));
  };

  const assignEmployee = async (employee: any) => {
    const managerId = selectedManager[employee.id];
    const storeId = selectedStore[employee.id];
    const manager = managers.find((m) => m.id === managerId);
    const managerStores = storesForManager(manager);
    if (!managerId) {
      setError('Select a manager.');
      return;
    }
    if (managerStores.length > 0 && !storeId) {
      setError('Select a store for this manager.');
      return;
    }
    try {
      setAssigning(employee.id);
      setError(null);
      await orgApi.updateEmployee(employee.id, {
        managerId,
        storeId: storeId || null,
        createdBy: manager?.userId || manager?.user?.id || employee.createdBy,
      });
      setSelectedManager((prev) => {
        const next = { ...prev };
        delete next[employee.id];
        return next;
      });
      setSelectedStore((prev) => {
        const next = { ...prev };
        delete next[employee.id];
        return next;
      });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setAssigning(null);
    }
  };

  const savePhone = async (id: string, phoneNumber: string) => {
    await orgApi.updateEmployee(id, { phoneNumber });
    await load();
  };

  if (creating) {
    return (
      <div style={pageStyle(600)}>
        <h2>Create New Employee</h2>
        <form onSubmit={create} style={formStyle}>
          {error && <div style={errorBanner}>{error}</div>}
          <div>
            <label style={labelStyle}>Full Name <span style={{ color: '#d32f2f' }}>*</span></label>
            <input required name="name" value={form.name || ''} placeholder="John Doe" onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email Address <span style={{ color: '#d32f2f' }}>*</span></label>
            <input required type="email" value={form.email || ''} placeholder="employee@company.com" onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Department</label>
            <input value={form.department || ''} placeholder="Engineering, Sales, Marketing, etc." onChange={(e) => setForm({ ...form, department: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>WhatsApp Phone Number</label>
            <input type="tel" value={form.phoneNumber || ''} placeholder="14155552671 (country code + number)" onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} style={inputStyle} />
            <div style={hintStyle}>Optional. Digits with country code — used for manager WhatsApp messaging via Wati.</div>
          </div>
          {stores.length > 1 && (
            <div>
              <label style={labelStyle}>Store <span style={{ color: '#d32f2f' }}>*</span></label>
              <select required value={form.storeId || ''} onChange={(e) => setForm({ ...form, storeId: e.target.value })} style={{ ...inputStyle, background: 'white' }}>
                <option value="">Select a store</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle}>Role <span style={{ color: '#d32f2f' }}>*</span></label>
            <select value={form.role || 'employee'} onChange={(e) => setForm({ ...form, role: e.target.value })} style={{ ...inputStyle, background: 'white' }}>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Temporary Password <span style={{ color: '#d32f2f' }}>*</span></label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input required value={form.temporaryPassword || ''} placeholder="Enter temporary password" onChange={(e) => setForm({ ...form, temporaryPassword: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={() => setForm({ ...form, temporaryPassword: generatePassword() })} style={{ padding: '0.75rem 1rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Generate
              </button>
            </div>
            <small style={{ color: '#666', fontSize: '0.8rem' }}>Password must contain uppercase, lowercase, number, and special character (min 8 chars)</small>
          </div>
          <div style={actionsStyle}>
            <button type="button" onClick={() => { setCreating(false); setError(null); }} style={cancelButton}>Cancel</button>
            <button type="submit" disabled={submitting} style={primaryButton(submitting)}>{submitting ? 'Creating Employee...' : 'Create Employee'}</button>
          </div>
        </form>
      </div>
    );
  }

  if (unassignedOnly) {
    const selectStyle: React.CSSProperties = {
      width: '100%',
      minWidth: 160,
      padding: '0.45rem 0.5rem',
      border: '1px solid #ccc',
      borderRadius: 4,
      background: 'white',
      fontFamily: 'inherit',
    };
    return (
      <Box>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>Unassigned Employees ({items.length})</h3>
          <button type="button" onClick={load} style={{ padding: '0.5rem 1rem', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
            🔄 Refresh
          </button>
        </div>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {items.length === 0 && <Typography>All employees are assigned to a manager.</Typography>}
        {items.length > 0 && (
          <div style={{ overflowX: 'auto', background: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1976d2', color: 'white', textAlign: 'left' }}>
                  {['Name', 'Email', 'Department', 'Created By', 'Managers', 'Store', 'Action'].map((col) => (
                    <th key={col} style={{ padding: '0.85rem 1rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((employee) => {
                  const managerId = selectedManager[employee.id] || '';
                  const manager = managers.find((m) => m.id === managerId);
                  const managerStores = storesForManager(manager);
                  const storeId = selectedStore[employee.id] || '';
                  const canAssign = Boolean(managerId) && (managerStores.length === 0 || Boolean(storeId));
                  return (
                    <tr key={employee.id} style={{ borderTop: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '0.85rem 1rem' }}>{employee.name}</td>
                      <td style={{ padding: '0.85rem 1rem' }}>{employee.email}</td>
                      <td style={{ padding: '0.85rem 1rem' }}>{employee.department || '—'}</td>
                      <td style={{ padding: '0.85rem 1rem' }}>{employee.createdByName || '—'}</td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <select value={managerId} onChange={(e) => chooseManager(employee.id, e.target.value)} style={selectStyle}>
                          <option value="">Select manager</option>
                          {managers.map((m) => (
                            <option key={m.id} value={m.id}>{m.name || m.email}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        {!managerId && <span style={{ color: '#999' }}>Select manager first</span>}
                        {managerId && managerStores.length === 0 && <span style={{ color: '#999' }}>No stores assigned</span>}
                        {managerId && managerStores.length > 0 && (
                          <select value={storeId} onChange={(e) => setSelectedStore((prev) => ({ ...prev, [employee.id]: e.target.value }))} style={selectStyle}>
                            <option value="">Select store</option>
                            {managerStores.map((store: any) => (
                              <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <button
                          type="button"
                          disabled={!canAssign || assigning === employee.id}
                          onClick={() => assignEmployee(employee)}
                          style={{
                            padding: '0.45rem 0.9rem',
                            backgroundColor: !canAssign || assigning === employee.id ? '#ccc' : '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: !canAssign || assigning === employee.id ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {assigning === employee.id ? 'Assigning...' : 'Assign'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Employees ({items.length})</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={() => setCreating(true)} style={{ padding: '0.55rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}>
            + Create Employee
          </button>
          <button type="button" onClick={load} style={{ padding: '0.55rem 1rem', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}>
            🔄 Refresh
          </button>
        </div>
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.length === 0 && <Typography>No employees found. Employees will appear here once they are added to the system.</Typography>}
      <div style={{ display: 'grid', gap: '1rem' }}>
      {items.map((e) => {
        const currentSchedule = schedulingResult;
        const schedule = currentSchedule && currentSchedule.employeeId === e.id ? currentSchedule.result : null;
        return (
        <div key={e.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.5rem', background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, color: '#1976d2' }}>{e.name}</h4>
              {e.isActive === false && <Chip size="small" label="INACTIVE" color="error" />}
              <Chip size="small" label={e.schedulingEligible ? 'SCHEDULING ELIGIBLE' : 'NOT ELIGIBLE FOR SCHEDULING'} color={e.schedulingEligible ? 'success' : 'warning'} />
            </div>
            <button
              type="button"
              title="Delete"
              aria-label={`Delete ${e.name}`}
              onClick={async () => {
                if (!window.confirm(`Delete employee "${e.name}"?`)) return;
                try {
                  setError(null);
                  await orgApi.deleteEmployee(e.id);
                  await load();
                } catch (err: any) {
                  setError(err?.response?.data?.error || err.message);
                }
              }}
              style={{
                width: 32,
                height: 32,
                padding: 0,
                backgroundColor: '#d32f2f',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              🗑️
            </button>
          </div>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', color: '#666' }}>
            <span><strong>Email:</strong> {e.email}</span>
            <span><strong>WhatsApp:</strong> {e.phoneNumber ? `+${String(e.phoneNumber).replace(/\D/g, '')}` : '—'}</span>
            {e.department && <span><strong>Department:</strong> {e.department}</span>}
            {e.manager?.name && <span><strong>Manager:</strong> {e.manager.name}</span>}
          </div>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button sx={{ fontSize: '1rem' }} onClick={async () => {
              const phone = window.prompt('WhatsApp number (country code, digits)', e.phoneNumber || '');
              if (phone != null) await savePhone(e.id, phone);
            }}>Set phone</Button>
            <Button
              sx={{ fontSize: '1rem' }}
              disabled={checkingId === e.id}
              onClick={async () => {
                try {
                  setCheckingId(e.id);
                  setError(null);
                  const r = await schedulingApi.checkEligibility(e.id);
                  setSchedulingResult({ employeeId: e.id, result: r.result });
                  await load();
                } catch (err: any) {
                  setError(err?.response?.data?.error || err.message);
                } finally {
                  setCheckingId(null);
                }
              }}
            >
              {checkingId === e.id ? 'Checking...' : 'Check scheduling'}
            </Button>
          </Stack>
          {schedule && (
            <Alert severity={schedule.eligible ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
              {schedule.message || (schedule.eligible ? 'Eligible for scheduling.' : 'Not eligible for scheduling.')}
            </Alert>
          )}
        </div>
        );
      })}
      </div>

    </Box>
  );
};

export default EmployeesPage;
